import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';
import { ArrowLeft, Edit, Package, Plus, Printer, Save, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { writeSanitizedHTML } from '../utils/safePrint';

const API = `${API_BASE_URL}/api`;

const ProcurementForm = ({ currentExercise, theme }) => {
  const API = API_BASE_URL + '/api';
  const [view, setView] = useState('list');
  const [entries, setEntries] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    exercise_name: currentExercise?.exercise_name || '',
    incident_name: '',
    procurement_period_from: '',
    procurement_period_to: '',
    procurement_officer: '',
    contact_info: '',
    supplier_vendors: [],
    contract_details: [],
    financial_admin: [],
    total_procurement_budget: '',
    total_expenditure: '',
    remaining_budget: '',
    procurement_notes: '',
    prepared_by: '',
    date_prepared: ''
  });
  const [loading, setLoading] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);

  const fetchEntries = async (silent = false) => {
    if (!currentExercise?.id) return;
    if (!silent) setLoading(true);
    try {
      const response = await fetch(`${API}/procurement?exercise_id=${currentExercise.id}`);
      if (response.ok) {
        const data = await response.json();
        setEntries(data);
        setLastSynced(new Date());
      }
    } catch (error) {
      if (!silent) console.error('Error fetching procurement entries:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [currentExercise?.id]);

  // Setup polling for real-time sync (every 30 seconds)
  useEffect(() => {
    if (currentExercise?.id) {
      const pollInterval = setInterval(() => {
        fetchEntries(true);
      }, 30000);
      
      return () => clearInterval(pollInterval);
    }
  }, [currentExercise?.id]);

  useEffect(() => {
    if (editingEntry) {
      setFormData({
        exercise_name: editingEntry.exercise_name || currentExercise?.exercise_name || '',
        incident_name: editingEntry.incident_name || '',
        procurement_period_from: editingEntry.procurement_period_from || '',
        procurement_period_to: editingEntry.procurement_period_to || '',
        procurement_officer: editingEntry.procurement_officer || '',
        contact_info: editingEntry.contact_info || '',
        supplier_vendors: editingEntry.supplier_vendors || [],
        contract_details: editingEntry.contract_details || [],
        financial_admin: editingEntry.financial_admin || [],
        total_procurement_budget: editingEntry.total_procurement_budget || '',
        total_expenditure: editingEntry.total_expenditure || '',
        remaining_budget: editingEntry.remaining_budget || '',
        procurement_notes: editingEntry.procurement_notes || '',
        prepared_by: editingEntry.prepared_by || '',
        date_prepared: editingEntry.date_prepared || ''
      });
    } else {
      setFormData({
        exercise_name: currentExercise?.exercise_name || '',
        incident_name: '',
        procurement_period_from: '',
        procurement_period_to: '',
        procurement_officer: '',
        contact_info: '',
        supplier_vendors: [],
        contract_details: [],
        financial_admin: [],
        total_procurement_budget: '',
        total_expenditure: '',
        remaining_budget: '',
        procurement_notes: '',
        prepared_by: '',
        date_prepared: ''
      });
    }
  }, [editingEntry, currentExercise]);

  // Supplier/Vendor handlers
  const addSupplierVendor = () => {
    setFormData(prev => ({
      ...prev,
      supplier_vendors: [...prev.supplier_vendors, {
        id: Date.now().toString(),
        company_name: '', contact_name: '', contact_email: '', contact_phone: '',
        business_number: '', indigenous_status: 'No', minority_status: 'No',
        gender_diversity: '', disability_status: 'No', lgbtq2_status: 'No',
        performance_rating: '3', restrictions: '', owners_info: '', ownership_percentage: ''
      }]
    }));
  };

  const updateSupplierVendor = (index, field, value) => {
    const updated = [...formData.supplier_vendors];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(prev => ({ ...prev, supplier_vendors: updated }));
  };

  const deleteSupplierVendor = (index) => {
    setFormData(prev => ({
      ...prev,
      supplier_vendors: prev.supplier_vendors.filter((_, i) => i !== index)
    }));
  };

  // Contract details handlers
  const addContractDetail = () => {
    setFormData(prev => ({
      ...prev,
      contract_details: [...prev.contract_details, {
        id: Date.now().toString(),
        contract_number: '', goods_services_description: '', solicitation_type: 'RFI',
        bid_submission_date: '', contract_value: '', contract_status: 'Draft',
        previous_purchases: ''
      }]
    }));
  };

  const updateContractDetail = (index, field, value) => {
    const updated = [...formData.contract_details];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(prev => ({ ...prev, contract_details: updated }));
  };

  const deleteContractDetail = (index) => {
    setFormData(prev => ({
      ...prev,
      contract_details: prev.contract_details.filter((_, i) => i !== index)
    }));
  };

  // Financial/Admin handlers
  const addFinancialAdmin = () => {
    setFormData(prev => ({
      ...prev,
      financial_admin: [...prev.financial_admin, {
        id: Date.now().toString(),
        budget_tracking_id: '', expenditure_amount: '', invoice_number: '',
        invoice_date: '', payment_status: 'Pending', compliance_notes: '',
        conflict_of_interest: 'No', value_for_money: 'Yes',
        delivery_certification: '', contractor_performance: ''
      }]
    }));
  };

  const updateFinancialAdmin = (index, field, value) => {
    const updated = [...formData.financial_admin];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(prev => ({ ...prev, financial_admin: updated }));
  };

  const deleteFinancialAdmin = (index) => {
    setFormData(prev => ({
      ...prev,
      financial_admin: prev.financial_admin.filter((_, i) => i !== index)
    }));
  };

  const saveEntry = async () => {
    if (!currentExercise?.id) {
      alert('No exercise selected');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        exercise_id: currentExercise.id,
        exercise_name: currentExercise.exercise_name
      };

      let response;
      if (editingEntry) {
        response = await fetch(`${API}/procurement/${editingEntry.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch(`${API}/procurement`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (response.ok) {
        alert(editingEntry ? 'Entry updated successfully!' : 'Entry created successfully!');
        fetchEntries();
        setView('list');
        setEditingEntry(null);
      } else {
        alert('Failed to save entry');
      }
    } catch (error) {
      console.error('Error saving entry:', error);
      alert('Error saving entry');
    } finally {
      setLoading(false);
    }
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    setLoading(true);
    try {
      const response = await fetch(`${API}/procurement/${entryId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Entry deleted successfully!');
        fetchEntries();
      } else {
        alert('Failed to delete entry');
      }
    } catch (error) {
      console.error('Error deleting entry:', error);
      alert('Error deleting entry');
    } finally {
      setLoading(false);
    }
  };

  // Comprehensive Print Function (DOM-safe via writeSanitizedHTML)
  const handlePrint = () => {
    const data = formData || {};
    const exerciseName = data.exercise_name || currentExercise?.exercise_name || 'N/A';
    const incidentName = data.incident_name || 'N/A';
    const generated = new Date().toLocaleString();

    const tableSection = (title, columns, rows, emptyMsg) => {
      if (!rows || rows.length === 0) {
        return `<div class="section"><h2>${title}</h2><p class="empty">${emptyMsg}</p></div>`;
      }
      const head = columns.map(c => `<th>${c.label}</th>`).join('');
      const body = rows.map(r =>
        `<tr>${columns.map(c => `<td>${r[c.key] != null && r[c.key] !== '' ? r[c.key] : ''}</td>`).join('')}</tr>`
      ).join('');
      return `
        <div class="section">
          <h2>${title} <span class="count">(${rows.length})</span></h2>
          <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
        </div>`;
    };

    const infoItem = (label, value) =>
      `<div class="info-item"><div class="info-label">${label}</div><div class="info-value">${value || 'N/A'}</div></div>`;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Procurement Report - ${exerciseName}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; line-height: 1.5; color: #111827; }
            h1 { color: #3b82f6; text-align: center; border-bottom: 3px solid #3b82f6; padding-bottom: 10px; margin-bottom: 6px; }
            .sub { text-align: center; color: #6b7280; margin-bottom: 24px; font-size: 12px; }
            h2 { color: #3b82f6; margin-top: 26px; border-bottom: 2px solid #e5e7eb; padding-bottom: 5px; font-size: 16px; }
            .count { color: #6b7280; font-size: 13px; font-weight: normal; }
            .section { margin-bottom: 22px; page-break-inside: avoid; }
            .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin: 10px 0; }
            .info-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin: 10px 0; }
            .info-grid-1 { display: grid; grid-template-columns: 1fr; gap: 10px; margin: 10px 0; }
            .info-item { padding: 8px 10px; background: #f9fafb; border-left: 3px solid #3b82f6; }
            .info-label { font-weight: bold; color: #6b7280; font-size: 11px; text-transform: uppercase; }
            .info-value { color: #111827; margin-top: 3px; font-size: 13px; white-space: pre-wrap; }
            table { width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 12px; }
            th, td { border: 1px solid #e5e7eb; padding: 6px 8px; text-align: left; vertical-align: top; }
            th { background: #eff6ff; font-weight: bold; color: #1e40af; }
            tr:nth-child(even) { background: #f9fafb; }
            .empty { color: #9ca3af; font-style: italic; font-size: 12px; }
            .footer { text-align: center; color: #6b7280; margin-top: 28px; padding-top: 12px; border-top: 1px solid #e5e7eb; font-size: 11px; }
            @media print { body { padding: 12px; } .section { page-break-inside: avoid; } }
          </style>
        </head>
        <body>
          <h1>Procurement Report</h1>
          <div class="sub"><strong>Exercise:</strong> ${exerciseName} &nbsp;•&nbsp; <strong>Incident:</strong> ${incidentName} &nbsp;•&nbsp; <strong>Generated:</strong> ${generated}</div>

          <div class="section">
            <h2>Basic Information</h2>
            <div class="info-grid">
              ${infoItem('Exercise Name', exerciseName)}
              ${infoItem('Incident Name', incidentName)}
              ${infoItem('Procurement Period From', data.procurement_period_from)}
              ${infoItem('Procurement Period To', data.procurement_period_to)}
              ${infoItem('Procurement Officer', data.procurement_officer)}
              ${infoItem('Contact Info', data.contact_info)}
            </div>
          </div>

          ${tableSection('Suppliers and Vendors',
            [
              { key: 'company_name', label: 'Company' },
              { key: 'contact_name', label: 'Contact' },
              { key: 'contact_email', label: 'Email' },
              { key: 'contact_phone', label: 'Phone' },
              { key: 'business_number', label: 'Biz #' },
              { key: 'indigenous_status', label: 'Indigenous' },
              { key: 'minority_status', label: 'Minority' },
              { key: 'gender_diversity', label: 'Gender' },
              { key: 'disability_status', label: 'Disability' },
              { key: 'lgbtq2_status', label: 'LGBTQ2' },
              { key: 'performance_rating', label: 'Perf' },
              { key: 'restrictions', label: 'Restrictions' },
              { key: 'owners_info', label: 'Owners' },
              { key: 'ownership_percentage', label: 'Ownership %' }
            ],
            data.supplier_vendors,
            'No suppliers/vendors.')}

          ${tableSection('Procurement & Contract Details',
            [
              { key: 'contract_number', label: 'Contract #' },
              { key: 'goods_services_description', label: 'Goods/Services' },
              { key: 'solicitation_type', label: 'Type' },
              { key: 'bid_submission_date', label: 'Bid Date' },
              { key: 'contract_value', label: 'Value ($)' },
              { key: 'contract_status', label: 'Status' },
              { key: 'previous_purchases', label: 'Previous Purchases' }
            ],
            data.contract_details,
            'No contract details.')}

          ${tableSection('Financial & Administrative',
            [
              { key: 'budget_tracking_id', label: 'Budget ID' },
              { key: 'expenditure_amount', label: 'Expenditure ($)' },
              { key: 'invoice_number', label: 'Invoice #' },
              { key: 'invoice_date', label: 'Invoice Date' },
              { key: 'payment_status', label: 'Payment' },
              { key: 'conflict_of_interest', label: 'Conflict' },
              { key: 'value_for_money', label: 'Value $$' },
              { key: 'delivery_certification', label: 'Delivery' },
              { key: 'contractor_performance', label: 'Performance' },
              { key: 'compliance_notes', label: 'Compliance' }
            ],
            data.financial_admin,
            'No financial/admin records.')}

          <div class="section">
            <h2>Budget Summary</h2>
            <div class="info-grid-3">
              ${infoItem('Total Procurement Budget ($)', data.total_procurement_budget)}
              ${infoItem('Total Expenditure ($)', data.total_expenditure)}
              ${infoItem('Remaining Budget ($)', data.remaining_budget)}
            </div>
            <div class="info-grid-1">
              ${infoItem('Procurement Notes', data.procurement_notes)}
            </div>
            <div class="info-grid">
              ${infoItem('Prepared By', data.prepared_by)}
              ${infoItem('Date Prepared', data.date_prepared)}
            </div>
          </div>

          <div class="footer">
            <div><strong>Procurement Report</strong> &nbsp;|&nbsp; ${exerciseName}</div>
            <div>Powered by EXRSIM</div>
          </div>
        </body>
      </html>
    `;

    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 250);
  };

  if (view === 'list') {
    return (
      <div className="p-6">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className={`text-3xl font-bold ${theme.colors.textPrimary} mb-2`}>Procurement</h1>
            <p className={`${theme.colors.textMuted}`}>Manage supplier information, contracts, and procurement budgets</p>
          </div>
          <Button onClick={() => setView('form')} className="bg-orange-500 hover:bg-orange-600">
            <Plus className="h-4 w-4 mr-2" />
            New Entry
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className={`${theme.colors.textMuted}`}>Loading...</p>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12">
            <Package className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
            <p className={`${theme.colors.textMuted} mb-4`}>No procurement entries found</p>
            <Button onClick={() => setView('form')} className="bg-orange-500 hover:bg-orange-600">
              Create First Entry
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {entries.map((entry) => (
              <Card key={entry.id} className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
                <CardHeader>
                  <CardTitle className={`${theme.colors.textPrimary}`}>
                    {entry.incident_name || 'Untitled Entry'}
                  </CardTitle>
                  <CardDescription className={`${theme.colors.textMuted}`}>
                    Period: {entry.procurement_period_from} - {entry.procurement_period_to} | Budget: ${entry.total_procurement_budget || '0'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditingEntry(entry);
                        setView('form');
                      }}
                      className="border-blue-500 text-blue-500 hover:bg-blue-500/10"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deleteEntry(entry.id)}
                      className="border-red-500 text-red-500 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6 flex justify-between items-center print:hidden">
        <Button onClick={() => {
          setView('list');
          setEditingEntry(null);
        }} variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to List
        </Button>
        <Button onClick={handlePrint} className="bg-blue-500 hover:bg-blue-600">
          <Printer className="h-4 w-4 mr-2" />
          Print Procurement Report
        </Button>
      </div>

      <div className="space-y-6">
        <div className="text-center mb-6">
          <h1 className={`text-3xl font-bold ${theme.colors.textPrimary} mb-2`}>Procurement Report</h1>
        </div>

        {/* Basic Info */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Exercise Name</Label>
                <div className={`p-2 bg-gray-800 rounded border border-gray-600`}>
                  <span className={`${theme.colors.textPrimary}`}>{currentExercise?.exercise_name || 'N/A'}</span>
                </div>
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Incident Name</Label>
                <Input
                  value={formData.incident_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, incident_name: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Procurement Period From</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={formData.procurement_period_from ? formData.procurement_period_from.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.procurement_period_from ? formData.procurement_period_from.split('T')[1] : '00:00';
                      setFormData(prev => ({ ...prev, procurement_period_from: `${date}T${time}` }));
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                  <Input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.procurement_period_from ? formData.procurement_period_from.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.procurement_period_from ? formData.procurement_period_from.split('T')[0] : '';
                        setFormData(prev => ({ ...prev, procurement_period_from: `${date}T${value}` }));
                      }
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                </div>
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Procurement Period To</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={formData.procurement_period_to ? formData.procurement_period_to.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.procurement_period_to ? formData.procurement_period_to.split('T')[1] : '00:00';
                      setFormData(prev => ({ ...prev, procurement_period_to: `${date}T${time}` }));
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                  <Input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.procurement_period_to ? formData.procurement_period_to.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.procurement_period_to ? formData.procurement_period_to.split('T')[0] : '';
                        setFormData(prev => ({ ...prev, procurement_period_to: `${date}T${value}` }));
                      }
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Procurement Officer</Label>
                <Input
                  value={formData.procurement_officer}
                  onChange={(e) => setFormData(prev => ({ ...prev, procurement_officer: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Contact Information</Label>
                <Input
                  value={formData.contact_info}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_info: e.target.value }))}
                  placeholder="Email or Phone"
                  className={`bg-gray-800 border-gray-600 text-white`}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Supplier & Vendor */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Supplier and Vendor Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {formData.supplier_vendors.map((item, index) => (
                <Card key={item.id} className={`${theme.colors.cardBackground} ${theme.colors.border} p-4`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <Input value={item.company_name} onChange={(e) => updateSupplierVendor(index, 'company_name', e.target.value)} placeholder="Company Name" className={`bg-gray-800 border-gray-600 text-white`} />
                    <Input value={item.contact_name} onChange={(e) => updateSupplierVendor(index, 'contact_name', e.target.value)} placeholder="Contact Name" className={`bg-gray-800 border-gray-600 text-white`} />
                    <Input value={item.contact_email} onChange={(e) => updateSupplierVendor(index, 'contact_email', e.target.value)} placeholder="Email" className={`bg-gray-800 border-gray-600 text-white`} />
                    <Input value={item.contact_phone} onChange={(e) => updateSupplierVendor(index, 'contact_phone', e.target.value)} placeholder="Phone" className={`bg-gray-800 border-gray-600 text-white`} />
                    <Input value={item.business_number} onChange={(e) => updateSupplierVendor(index, 'business_number', e.target.value)} placeholder="Business Number" className={`bg-gray-800 border-gray-600 text-white`} />
                    <Select value={item.indigenous_status} onValueChange={(value) => updateSupplierVendor(index, 'indigenous_status', value)}>
                      <SelectTrigger className={`bg-gray-800 border-gray-600 text-white`}>
                        <SelectValue placeholder="Indigenous Status" />
                      </SelectTrigger>
                      <SelectContent><SelectItem value="Yes">Indigenous: Yes</SelectItem><SelectItem value="No">Indigenous: No</SelectItem></SelectContent>
                    </Select>
                    <Select value={item.minority_status} onValueChange={(value) => updateSupplierVendor(index, 'minority_status', value)}>
                      <SelectTrigger className={`bg-gray-800 border-gray-600 text-white`}>
                        <SelectValue placeholder="Minority Status" />
                      </SelectTrigger>
                      <SelectContent><SelectItem value="Yes">Visible Minority: Yes</SelectItem><SelectItem value="No">Visible Minority: No</SelectItem></SelectContent>
                    </Select>
                    <Input value={item.gender_diversity} onChange={(e) => updateSupplierVendor(index, 'gender_diversity', e.target.value)} placeholder="Gender" className={`bg-gray-800 border-gray-600 text-white`} />
                    <Select value={item.disability_status} onValueChange={(value) => updateSupplierVendor(index, 'disability_status', value)}>
                      <SelectTrigger className={`bg-gray-800 border-gray-600 text-white`}>
                        <SelectValue placeholder="Disability Status" />
                      </SelectTrigger>
                      <SelectContent><SelectItem value="Yes">Disability: Yes</SelectItem><SelectItem value="No">Disability: No</SelectItem></SelectContent>
                    </Select>
                    <Select value={item.lgbtq2_status} onValueChange={(value) => updateSupplierVendor(index, 'lgbtq2_status', value)}>
                      <SelectTrigger className={`bg-gray-800 border-gray-600 text-white`}>
                        <SelectValue placeholder="LGBTQ2 Status" />
                      </SelectTrigger>
                      <SelectContent><SelectItem value="Yes">LGBTQ2: Yes</SelectItem><SelectItem value="No">LGBTQ2: No</SelectItem></SelectContent>
                    </Select>
                    <Select value={item.performance_rating} onValueChange={(value) => updateSupplierVendor(index, 'performance_rating', value)}>
                      <SelectTrigger className={`bg-gray-800 border-gray-600 text-white`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent><SelectItem value="5">Performance: Excellent</SelectItem><SelectItem value="4">Performance: Good</SelectItem><SelectItem value="3">Performance: Average</SelectItem><SelectItem value="2">Performance: Below Average</SelectItem><SelectItem value="1">Performance: Poor</SelectItem></SelectContent>
                    </Select>
                    <Input value={item.restrictions} onChange={(e) => updateSupplierVendor(index, 'restrictions', e.target.value)} placeholder="Restrictions" className={`bg-gray-800 border-gray-600 text-white`} />
                    <Input value={item.owners_info} onChange={(e) => updateSupplierVendor(index, 'owners_info', e.target.value)} placeholder="Owners Information" className={`bg-gray-800 border-gray-600 text-white`} />
                    <Input value={item.ownership_percentage} onChange={(e) => updateSupplierVendor(index, 'ownership_percentage', e.target.value)} placeholder="Ownership %" className={`bg-gray-800 border-gray-600 text-white`} />
                  </div>
                  <Button variant="outline" size="sm" onClick={() => deleteSupplierVendor(index)} className="border-red-500 text-red-500 hover:bg-red-500/10 print:hidden">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Supplier
                  </Button>
                </Card>
              ))}
            </div>
            <div className="mt-4 print:hidden">
              <Button onClick={addSupplierVendor} variant="outline" className="border-orange-500 text-orange-500 hover:bg-orange-500/10">
                <Plus className="h-4 w-4 mr-2" />
                Add Supplier/Vendor
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Procurement & Contract Details */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Procurement and Contract Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {formData.contract_details.map((item, index) => (
                <Card key={item.id} className={`${theme.colors.cardBackground} ${theme.colors.border} p-4`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <Input value={item.contract_number} onChange={(e) => updateContractDetail(index, 'contract_number', e.target.value)} placeholder="Contract Number" className={`bg-gray-800 border-gray-600 text-white`} />
                    <Input value={item.goods_services_description} onChange={(e) => updateContractDetail(index, 'goods_services_description', e.target.value)} placeholder="Goods/Services Description" className={`bg-gray-800 border-gray-600 text-white`} />
                    <Select value={item.solicitation_type} onValueChange={(value) => updateContractDetail(index, 'solicitation_type', value)}>
                      <SelectTrigger className={`bg-gray-800 border-gray-600 text-white`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent><SelectItem value="RFI">RFI</SelectItem><SelectItem value="RFP">RFP</SelectItem><SelectItem value="RFQ">RFQ</SelectItem><SelectItem value="P&A">P&A</SelectItem></SelectContent>
                    </Select>
                    <Input type="date" value={item.bid_submission_date} onChange={(e) => updateContractDetail(index, 'bid_submission_date', e.target.value)} placeholder="Bid Submission Date" className={`bg-gray-800 border-gray-600 text-white`} />
                    <Input value={item.contract_value} onChange={(e) => updateContractDetail(index, 'contract_value', e.target.value)} placeholder="Contract Value ($)" className={`bg-gray-800 border-gray-600 text-white`} />
                    <Select value={item.contract_status} onValueChange={(value) => updateContractDetail(index, 'contract_status', value)}>
                      <SelectTrigger className={`bg-gray-800 border-gray-600 text-white`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent><SelectItem value="Draft">Draft</SelectItem><SelectItem value="Submitted">Submitted</SelectItem><SelectItem value="Under Review">Under Review</SelectItem><SelectItem value="Awarded">Awarded</SelectItem><SelectItem value="Completed">Completed</SelectItem></SelectContent>
                    </Select>
                    <Input value={item.previous_purchases} onChange={(e) => updateContractDetail(index, 'previous_purchases', e.target.value)} placeholder="Previous Purchases" className={`bg-gray-800 border-gray-600 text-white md:col-span-2`} />
                  </div>
                  <Button variant="outline" size="sm" onClick={() => deleteContractDetail(index)} className="border-red-500 text-red-500 hover:bg-red-500/10 print:hidden">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Contract
                  </Button>
                </Card>
              ))}
            </div>
            <div className="mt-4 print:hidden">
              <Button onClick={addContractDetail} variant="outline" className="border-orange-500 text-orange-500 hover:bg-orange-500/10">
                <Plus className="h-4 w-4 mr-2" />
                Add Contract Detail
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Financial & Administrative */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Financial and Administrative Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {formData.financial_admin.map((item, index) => (
                <Card key={item.id} className={`${theme.colors.cardBackground} ${theme.colors.border} p-4`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <Input value={item.budget_tracking_id} onChange={(e) => updateFinancialAdmin(index, 'budget_tracking_id', e.target.value)} placeholder="Budget Tracking ID" className={`bg-gray-800 border-gray-600 text-white`} />
                    <Input value={item.expenditure_amount} onChange={(e) => updateFinancialAdmin(index, 'expenditure_amount', e.target.value)} placeholder="Expenditure Amount ($)" className={`bg-gray-800 border-gray-600 text-white`} />
                    <Input value={item.invoice_number} onChange={(e) => updateFinancialAdmin(index, 'invoice_number', e.target.value)} placeholder="Invoice Number" className={`bg-gray-800 border-gray-600 text-white`} />
                    <Input type="date" value={item.invoice_date} onChange={(e) => updateFinancialAdmin(index, 'invoice_date', e.target.value)} placeholder="Invoice Date" className={`bg-gray-800 border-gray-600 text-white`} />
                    <Select value={item.payment_status} onValueChange={(value) => updateFinancialAdmin(index, 'payment_status', value)}>
                      <SelectTrigger className={`bg-gray-800 border-gray-600 text-white`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent><SelectItem value="Pending">Pending</SelectItem><SelectItem value="Approved">Approved</SelectItem><SelectItem value="Paid">Paid</SelectItem><SelectItem value="Overdue">Overdue</SelectItem></SelectContent>
                    </Select>
                    <Select value={item.conflict_of_interest} onValueChange={(value) => updateFinancialAdmin(index, 'conflict_of_interest', value)}>
                      <SelectTrigger className={`bg-gray-800 border-gray-600 text-white`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent><SelectItem value="No">Conflict of Interest: No</SelectItem><SelectItem value="Yes">Conflict of Interest: Yes</SelectItem></SelectContent>
                    </Select>
                    <Select value={item.value_for_money} onValueChange={(value) => updateFinancialAdmin(index, 'value_for_money', value)}>
                      <SelectTrigger className={`bg-gray-800 border-gray-600 text-white`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent><SelectItem value="Yes">Value for Money: Yes</SelectItem><SelectItem value="No">Value for Money: No</SelectItem></SelectContent>
                    </Select>
                    <Input value={item.delivery_certification} onChange={(e) => updateFinancialAdmin(index, 'delivery_certification', e.target.value)} placeholder="Delivery Certification" className={`bg-gray-800 border-gray-600 text-white`} />
                    <Input value={item.contractor_performance} onChange={(e) => updateFinancialAdmin(index, 'contractor_performance', e.target.value)} placeholder="Contractor Performance" className={`bg-gray-800 border-gray-600 text-white`} />
                    <Input value={item.compliance_notes} onChange={(e) => updateFinancialAdmin(index, 'compliance_notes', e.target.value)} placeholder="Compliance Notes" className={`bg-gray-800 border-gray-600 text-white md:col-span-2`} />
                  </div>
                  <Button variant="outline" size="sm" onClick={() => deleteFinancialAdmin(index)} className="border-red-500 text-red-500 hover:bg-red-500/10 print:hidden">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Financial Record
                  </Button>
                </Card>
              ))}
            </div>
            <div className="mt-4 print:hidden">
              <Button onClick={addFinancialAdmin} variant="outline" className="border-orange-500 text-orange-500 hover:bg-orange-500/10">
                <Plus className="h-4 w-4 mr-2" />
                Add Financial Record
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Budget Summary */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Budget Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Total Procurement Budget ($)</Label>
                <Input value={formData.total_procurement_budget} onChange={(e) => setFormData(prev => ({ ...prev, total_procurement_budget: e.target.value }))} placeholder="0.00" className={`bg-gray-800 border-gray-600 text-white`} />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Total Expenditure ($)</Label>
                <Input value={formData.total_expenditure} onChange={(e) => setFormData(prev => ({ ...prev, total_expenditure: e.target.value }))} placeholder="0.00" className={`bg-gray-800 border-gray-600 text-white`} />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Remaining Budget ($)</Label>
                <Input value={formData.remaining_budget} onChange={(e) => setFormData(prev => ({ ...prev, remaining_budget: e.target.value }))} placeholder="0.00" className={`bg-gray-800 border-gray-600 text-white`} />
              </div>
            </div>
            <div>
              <Label className={`text-white text-xs font-medium mb-1 block`}>Procurement Notes</Label>
              <Textarea value={formData.procurement_notes} onChange={(e) => setFormData(prev => ({ ...prev, procurement_notes: e.target.value }))} rows={3} placeholder="Additional notes..." className={`bg-gray-800 border-gray-600 text-white`} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Prepared By</Label>
                <Input value={formData.prepared_by} onChange={(e) => setFormData(prev => ({ ...prev, prepared_by: e.target.value }))} className={`bg-gray-800 border-gray-600 text-white`} />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Date Prepared</Label>
                <Input type="date" value={formData.date_prepared} onChange={(e) => setFormData(prev => ({ ...prev, date_prepared: e.target.value }))} className={`bg-gray-800 border-gray-600 text-white`} />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end print:hidden">
          <Button onClick={saveEntry} disabled={loading} className="bg-orange-500 hover:bg-orange-600">
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Saving...' : (editingEntry ? 'Update' : 'Save')} Entry
          </Button>
        </div>
      </div>
    </div>
  );
};

// ICS 259 - Resource Order Aircraft Form

export default ProcurementForm;
